import { forwardRef, useImperativeHandle, useRef, type ReactNode } from 'react';
import { clsx } from 'clsx';

import styles from './Checkbox.module.scss';

export interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  className?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
}

export interface CheckboxRef {
  focus: () => void;
}

const Checkbox = forwardRef<CheckboxRef, CheckboxProps>(
  ({ checked, onChange, label, className, disabled = false, id, name }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current?.focus();
      },
    }));

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!disabled) {
        onChange(event.target.checked);
      }
    };

    return (
      <label className={clsx(styles.wrapper, className, disabled && styles.disabled)}>
        <input
          ref={inputRef}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          className={styles.input}
          id={id}
          name={name}
          onChange={handleChange}
        />

        <div className={clsx(styles.checkboxBox, checked && styles.checked, disabled && styles.disabled)}>
          {checked && (
            <svg
              className={styles.checkmark}
              width="12"
              height="10"
              viewBox="0 0 12 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 5L4.5 8.5L11 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>
        {label && <span className={styles.label}>{label}</span>}
      </label>
    );
  },
);
Checkbox.displayName = 'Checkbox';

export default Checkbox;
